import { __, sprintf } from '@wordpress/i18n';
import { Spinner, Notice } from '@wordpress/components';

import useDynamicUsers from './use-dynamic-users';
import { getInitials } from '../../Avatar/edit';

/**
 * Live editor preview for dynamic sources. Fetches the same users the server
 * would (via core-data) and renders the frontend markup (`.brandy-avatar-item`)
 * clamped to maxVisible with a static +N tile. Sizing/ring/overlap inherit from
 * the CSS variables on the parent wrapper set in Edit/index.jsx.
 */
function avatarUrl(user) {
  const urls = user.avatar_urls || {};
  return urls['96'] || urls['48'] || urls['24'] || '';
}

export default function DynamicPreview({ attributes }) {
  const { source, query, userIds, maxVisible, shape, overflowPrefix } = attributes;
  const { users, isResolving } = useDynamicUsers(source, query, userIds);

  if (isResolving && !users.length) {
    return <Spinner />;
  }

  if (!users.length) {
    return (
      <Notice status="warning" isDismissible={false}>
        {source === 'specific'
          ? __('Select one or more users to display.', 'brandy-blocks')
          : __('No matching users found for this source.', 'brandy-blocks')}
      </Notice>
    );
  }

  const visible = users.slice(0, maxVisible);
  const hidden = Math.max(0, users.length - maxVisible);

  return (
    <ul className="brandy-avatar-group__list">
      {visible.map((user) => {
        const url = avatarUrl(user);
        return (
          <li key={user.id} className="brandy-avatar-item" data-name={user.name || undefined}>
            <span className={`brandy-avatar is-shape-${shape}`}>
              {url ? (
                <img className="brandy-avatar__img" src={url} alt={user.name} />
              ) : (
                <span className="brandy-avatar__initials" aria-hidden="true">
                  {getInitials(user.name)}
                </span>
              )}
            </span>
          </li>
        );
      })}
      {hidden > 0 && (
        <li className="brandy-avatar-overflow">
          <span
            className={`brandy-avatar brandy-avatar-overflow__btn is-shape-${shape}`}
            aria-hidden="true"
          >
            {`${overflowPrefix}${hidden}`}
          </span>
        </li>
      )}
    </ul>
  );
}
